import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, ChevronLeft, ChevronRight, Lock, UserCheck, Sparkles, ArrowRight, LayoutGrid, MessageCircle, CheckCircle2, Link as LinkIcon, ExternalLink, Home, LogOut, FileText, ClipboardList } from 'lucide-react';
import API_URL from '../config';
import LoadingScreen from '../components/LoadingScreen';
import FacultyAdmin from './faculty/FacultyAdmin';
import './Home.css';

const CountdownDisplay = ({ expiresAt }) => {
    const [timeLeft, setTimeLeft] = useState("");

    useEffect(() => {
        const calculateTimeLeft = () => {
            const now = new Date();
            const end = new Date(expiresAt);
            const diff = end - now;

            if (diff <= 0) {
                setTimeLeft("Closed");
                return false;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const mins = Math.floor((diff / (1000 * 60)) % 60);
            const secs = Math.floor((diff / 1000) % 60);

            let str = "closing in ";
            if (days > 0) str += `${days}d `;
            if (hours > 0 || days > 0) str += `${hours}hrs `;
            str += `${mins}m ${secs}s`;
            setTimeLeft(str);
            return true;
        };



        // Initial call
        const isActive = calculateTimeLeft();
        if (!isActive) return;

        const timer = setInterval(() => {
            const stillActive = calculateTimeLeft();
            if (!stillActive) clearInterval(timer);
        }, 1000);

        return () => clearInterval(timer);
    }, [expiresAt]);

    return (
        <p style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '0.9rem', marginTop: 4 }}>
            {timeLeft}
        </p>
    );
};

export default function Formation() {
    const { taskId } = useParams();
    const navigate = useNavigate();

    // State for Hub View
    const [tasksList, setTasksList] = useState([]);

    // State for Detail View
    const [task, setTask] = useState(null);
    const [teams, setTeams] = useState([]);
    const [collaborators, setCollaborators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTeamName, setNewTeamName] = useState("");
    const [membership, setMembership] = useState({ inTeam: false });
    const [activeTab, setActiveTab] = useState("teams");

    const user = JSON.parse(localStorage.getItem("vjconnect_user") || "{}");

    useEffect(() => {
        if (!user || !user.email) return;

        if (taskId) {
            fetchDetailData();
        } else {
            fetchHubData();
        }
    }, [taskId]); // Only re-run when taskId changes, not on every user reference change

    const fetchHubData = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/formation/tasks?email=${user.email}&role=${user.role || ''}`); // Pass email and role for visibility filtering
            const data = await res.json();
            setTasksList(data.tasks || []);
        } catch (err) {
            console.error("Hub fetch error", err);
        } finally {
            setLoading(false);
        }
    };



    // Scroll Preservation
    useEffect(() => {
        if (taskId && !loading && teams.length > 0) {
            const savedScroll = sessionStorage.getItem(`formation_scroll_${taskId}`);
            if (savedScroll) {
                window.scrollTo(0, parseInt(savedScroll, 10));
            }
        }
    }, [taskId, loading, teams.length]);

    const handleTeamClick = (teamId) => {
        sessionStorage.setItem(`formation_scroll_${taskId}`, window.scrollY);
        navigate(`/formation/team/${teamId}`);
    };

    const fetchDetailData = async () => {
        setLoading(true);
        try {
            // Fetch specific task
            // We can re-use the list endpoint or simpler if there's a specific one, but list is fine
            // Ideally backend should have /formation/tasks/:id, but for now filtering list is okay or we update backend.
            // Let's rely on list for now to keep it simple, or careful if list is filtered? 
            // If I filter by visibility, I might not see it if I'm not allowed? Correct.

            const taskRes = await fetch(`${API_URL}/formation/tasks?email=${user.email}&role=${user.role || ''}`);
            const taskData = await taskRes.json();
            const currentTask = (taskData.tasks || []).find(t => String(t.id) === taskId);

            if (currentTask) {
                setTask(currentTask);

                // Fetch teams
                const teamsRes = await fetch(`${API_URL}/formation/tasks/${taskId}/teams`);
                const teamsData = await teamsRes.json();
                setTeams(teamsData.teams || []);

                // Check membership
                const memRes = await fetch(`${API_URL}/formation/tasks/${taskId}/membership?email=${user.email}`);
                const memData = await memRes.json();
                setMembership(memData);

                // Fetch collaborators
                const collabRes = await fetch(`${API_URL}/teams/tasks/${taskId}/collaborators`);
                const collabData = await collabRes.json();
                if (collabData.collaborators) setCollaborators(collabData.collaborators);
            } else {
                // Task not found or not visible
                setTask(null);
            }

        } catch (err) {
            console.error("Detail fetch error", err);
        } finally {
            setLoading(false);
        }
    };



    const handleCreateTeam = async () => {
        if (!newTeamName.trim()) return;
        try {
            const res = await fetch(`${API_URL}/formation/teams`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    task_id: taskId,
                    team_name: newTeamName,
                    lead_email: user.email
                })
            });
            if (res.ok) {
                const data = await res.json();
                navigate(`/formation/team/${data.teamId}`);
            } else {
                const err = await res.json();
                alert(err.error || "Failed to create team");
            }
        } catch (err) {
            console.error("Create team error", err);
        }
    };

    if (loading) return <LoadingScreen message="Formation Hub" />;

    // --- HUB VIEW (List of Tasks) ---
    if (!taskId) {
        return (
            <div className="home-page-container">
                <div className="home-content-wrapper">
                    <header className="home-header">
                        <div className="brand-section">
                            <h1 className="brand-title">Formation Hub</h1>
                            <p style={{ color: 'var(--text-main)', opacity: 0.6, marginTop: 4 }}>
                                Select a formation event to participate in
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                            <button
                                onClick={() => navigate(user?.role === 'faculty' ? '/faculty/home' : '/home')}
                                title="Home"
                                style={{
                                    width: 46, height: 46, borderRadius: '14px',
                                    border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                                    color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0
                                }}
                            >
                                <Home size={20} />
                            </button>
                            <button
                                onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }}
                                title="Logout"
                                style={{
                                    width: 46, height: 46, borderRadius: '14px',
                                    border: 'none', background: '#ef4444',
                                    color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0
                                }}
                            >
                                <LogOut size={20} />
                            </button>
                        </div>
                    </header>

                    <div className="features-grid-bento" style={{ marginTop: '2rem' }}>
                        {tasksList.length === 0 ? (
                            <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', gridColumn: '1/-1' }}>
                                <LayoutGrid size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                <p>No active formation tasks available for you right now.</p>
                            </div>
                        ) : (
                            tasksList.map(t => (
                                <motion.div
                                    key={t.id}
                                    whileHover={{ scale: 1.02 }}
                                    className="glass-card"
                                    style={{ padding: '2rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '1rem' }}
                                    onClick={() => navigate(`/formation/${t.id}`)}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div className="tile-icon-box" style={{ background: t.status === 'locked' ? '#ef4444' : 'var(--primary)', width: 50, height: 50 }}>
                                            {t.status === 'locked' ? <Lock size={24} color="white" /> : <Users size={24} color="white" />}
                                        </div>
                                        <div className={t.status === 'locked' ? "status-badge-closed" : "status-badge-open"}>
                                            {t.status === 'locked' ? 'Locked' : 'Active'}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{t.name}</h3>
                                        <p style={{ opacity: 0.6, fontSize: '0.9rem' }}>
                                            Max Members: <strong>{t.max_members}</strong> | Created by: <strong>{t.display_creator_name}</strong>
                                        </p>
                                        {t.expires_at && (
                                            <CountdownDisplay expiresAt={t.expires_at} />
                                        )}
                                    </div>

                                    <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', color: 'var(--accent-color)', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                            View Teams <ArrowRight size={16} style={{ marginLeft: 8 }} />
                                        </div>
                                        {t.is_reviewer && (
                                            <button
                                                onClick={e => { e.stopPropagation(); navigate(`/formation/${t.id}/review`); }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '6px',
                                                    padding: '5px 12px', borderRadius: '10px', border: 'none',
                                                    background: 'linear-gradient(135deg,#f59e0b,#ef4444)', color: '#fff',
                                                    cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem'
                                                }}
                                            >
                                                <ClipboardList size={13} /> Review Dashboard
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // --- DETAIL VIEW (Teams in a Task) ---
    if (!task) return <div className="not-found">Task not found or access denied.</div>;

    // Check query params for "generated"
    const params = new URLSearchParams(window.location.search);
    const generatedCount = params.get('generated');

    return (
        <div className="home-page-container">
            {generatedCount && (
                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: '#22c55e', color: 'white', padding: '12px 24px', borderRadius: 30, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 10 }}
                >
                    <Sparkles size={20} fill="white" />
                    Generated {generatedCount} teams successfully! Room is now Locked.
                </motion.div>
            )}

            <div className="home-content-wrapper">
                <header className="home-header">
                    <div className="brand-section">
                        <h1 className="brand-title">{task.name}</h1>
                        <p style={{ color: 'var(--text-main)', opacity: 0.6, marginTop: 4 }}>
                            Team Formation Hub - Max {task.max_members} members <span style={{ marginLeft: 10, background: task.status === 'locked' ? '#ef4444' : '#22c55e', color: 'white', padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>{task.status.toUpperCase()}</span>
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, alignItems: 'center' }}>
                        {(user?.role === 'faculty' || user?.role === 'admin' || task?.is_reviewer) && (
                            <button
                                onClick={() => navigate(`/formation/${task.id}/review`)}
                                title="Review Dashboard"
                                style={{
                                    height: 46, padding: '0 16px', borderRadius: '14px', border: 'none',
                                    background: 'linear-gradient(135deg,#f59e0b,#ef4444)', color: '#ffffff',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                                    fontWeight: 700, fontSize: '0.85rem', boxShadow: '0 2px 8px rgba(245,158,11,0.3)', flexShrink: 0
                                }}
                            >
                                <ClipboardList size={18} /> Review Dashboard
                            </button>
                        )}
                        <button
                            onClick={() => navigate('/formation')}
                            title="Back"
                            style={{
                                width: 46, height: 46, borderRadius: '14px',
                                border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                                color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0
                            }}
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            onClick={() => navigate(user?.role === 'faculty' ? '/faculty/home' : '/home')}
                            title="Home"
                            style={{
                                width: 46, height: 46, borderRadius: '14px',
                                border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                                color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0
                            }}
                        >
                            <Home size={20} />
                        </button>
                        <button
                            onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }}
                            title="Logout"
                            style={{
                                width: 46, height: 46, borderRadius: '14px',
                                border: 'none', background: '#ef4444',
                                color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0
                            }}
                        >
                            <LogOut size={20} />
                        </button>
                    </div>
                </header>

                {task.expires_at && (
                    <div style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        borderRadius: '8px',
                        padding: '10px',
                        marginTop: '1.5rem',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                    }}>
                        <motion.div
                            animate={{ x: [1000, -1500] }}
                            transition={{ repeat: Infinity, duration: 30, ease: "linear" }}
                            style={{ color: "#f59e0b", fontWeight: "bold", fontSize: "1rem" }}
                        >

                        </motion.div>
                        <motion.div
                            animate={{ x: [1000, -1500] }}
                            transition={{ repeat: Infinity, duration: 25, ease: "linear" }}
                            style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '1rem' }}
                        >
                            The team formation for "{task.name}" will be closed by {new Date(task.expires_at).toLocaleString()}. Lock your teams as soon as possible!
                        </motion.div>
                    </div>
                )}

                {collaborators.length > 0 && (
                    <div className="glass-card" style={{ padding: '1rem 1.5rem', marginTop: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--brand-primary)', fontWeight: '600' }}>
                            <ShieldCheck size={18} /> Faculty Coordinators:
                        </div>
                        {collaborators.map(c => (
                            <div key={c.email} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', opacity: 0.9 }}>
                                <div style={{ height: 6, width: 6, borderRadius: '50%', background: 'var(--brand-primary)' }} />
                                <span>{c.name} ({c.emp_id})</span>
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', borderBottom: '1px solid var(--glass-border)', paddingBottom: '1rem' }}>
                    <button
                        onClick={() => setActiveTab('teams')}
                        className={`btn-profile ${activeTab === 'teams' ? 'btn-profile-primary' : 'btn-profile-secondary'}`}
                        style={{ padding: '8px 16px', fontSize: '1rem' }}
                    >
                        <Users size={18} style={{ marginRight: 8 }} /> Teams
                    </button>
                    <button
                        onClick={() => setActiveTab('problems')}
                        className={`btn-profile ${activeTab === 'problems' ? 'btn-profile-primary' : 'btn-profile-secondary'}`}
                        style={{ padding: '8px 16px', fontSize: '1rem' }}
                    >
                        <FileText size={18} style={{ marginRight: 8 }} /> Check Problem Statements
                    </button>
                </div>

                {(() => {
                    const isExcelRoom = (() => {
                        if (!task) return false;
                        let rules = {};
                        let visRules = {};
                        try { rules = typeof task.rules === 'string' ? JSON.parse(task.rules) : (task.rules || {}); } catch(e){}
                        try { visRules = typeof task.visibility_rules === 'string' ? JSON.parse(task.visibility_rules) : (task.visibility_rules || {}); } catch(e){}
                        return !!(rules.excel_created || visRules.creation_mode === 'excel' || (visRules.restricted_roll_nos && visRules.restricted_roll_nos.length > 0));
                    })();

                    return activeTab === 'teams' ? (
                        <section className="teams-grid-section" style={{ marginTop: '2rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h2 className="section-title">Active Teams</h2>
                                {!membership.inTeam ? (
                                    (!isExcelRoom && task.status !== 'locked') ? (
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className="btn-profile btn-profile-primary"
                                            onClick={() => setShowCreateModal(true)}
                                        >
                                            <Plus size={18} style={{ marginRight: 8 }} /> Create Team
                                        </motion.button>
                                    ) : (
                                        <div style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(239,68,68,0.1)', padding: '8px 16px', borderRadius: 20, fontSize: '0.85rem', fontWeight: 'bold' }}>
                                            <Lock size={16} /> {isExcelRoom ? 'Pre-formed Roster' : 'Room Locked'}
                                        </div>
                                    )
                                ) : (
                                    <button
                                        className="btn-profile btn-profile-secondary"
                                        onClick={() => handleTeamClick(membership.teamId)}
                                    >
                                        <UserCheck size={18} style={{ marginRight: 8 }} /> My Team
                                    </button>
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                                {teams.length === 0 ? (
                                    <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', gridColumn: '1/-1' }}>
                                        <Users size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                        <p>No teams yet.</p>
                                    </div>
                                ) : (
                                    teams.map(team => {
                                        // Calculate dynamic glow class based on combinations
                                        const wa = !!team.whatsapp_link;
                                        const ps = !!team.problem_statement;
                                        const ppt = !!(team.ppt_links && team.ppt_links !== '[]');

                                        let glowClass = "";
                                        if (wa && ps && ppt) glowClass = "team-card-glow glow-all";
                                        else if (wa && ps) glowClass = "team-card-glow glow-wa-ps";
                                        else if (wa && ppt) glowClass = "team-card-glow glow-wa-ppt";
                                        else if (ps && ppt) glowClass = "team-card-glow glow-ps-ppt";
                                        else if (wa) glowClass = "team-card-glow glow-wa";
                                        else if (ps) glowClass = "team-card-glow glow-ps";
                                        else if (ppt) glowClass = "team-card-glow glow-ppt";

                                        return (
                                            <div
                                                key={team.id}
                                                className={`search-result-row glass-card ${glowClass}`}
                                                style={{ padding: '1.5rem', cursor: 'pointer', flexDirection: 'column', alignItems: 'flex-start' }}
                                                onClick={() => handleTeamClick(team.id)}
                                            >
                                                <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span style={{ fontSize: '0.8rem', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '1px' }}>TEAM #{team.team_number}</span>
                                                        <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{team.team_name}</h3>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {team.status === 'locked' ? <Lock size={16} color="#ef4444" /> : <div className="status-badge-open">Open</div>}
                                                    </div>
                                                </div>

                                                {/* Status Icons Row */}
                                                <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
                                                    <div className={`status-icon-badge whatsapp ${team.whatsapp_link ? 'active' : ''}`} title={team.whatsapp_link ? "WhatsApp Group Linked" : "No WhatsApp Link"}>
                                                        <MessageCircle size={16} />
                                                    </div>
                                                    <div className={`status-icon-badge ps ${team.problem_statement ? 'active' : ''}`} title={team.problem_statement ? "Problem Statement Added" : "No Problem Statement"}>
                                                        <CheckCircle2 size={16} />
                                                    </div>
                                                    <div className={`status-icon-badge ppt ${(team.ppt_links && team.ppt_links !== '[]') ? 'active' : ''}`} title={(team.ppt_links && team.ppt_links !== '[]') ? "PPT Links Shared" : "No PPT Links"}>
                                                        <LinkIcon size={16} />
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                                                    {team.members.map(m => (
                                                        <div key={m.email} className="member-dot" title={`${m.name} (${m.department})`}>
                                                            {m.profile_pic ? <img src={m.profile_pic} alt="" /> : m.name.charAt(0)}
                                                        </div>
                                                    ))}
                                                    {(() => {
                                                        if (isExcelRoom || team.is_auto_generated || task.status === 'locked') return null;
                                                        const maxMembers = Number(task.max_members) || 0;
                                                        const memberCount = Array.isArray(team.members) ? team.members.length : 0;
                                                        const emptySlots = Math.max(0, maxMembers - memberCount);
                                                        return Array(emptySlots).fill(0).map((_, i) => (
                                                            <div key={i} className="member-dot-empty" title="Empty Slot">
                                                                <Plus size={12} />
                                                            </div>
                                                        ));
                                                    })()}
                                                </div>

                                            {team.status !== 'locked' && !membership.inTeam && team.members.length < task.max_members && (
                                                <button
                                                    className="btn-profile btn-profile-primary"
                                                    style={{ marginTop: '0.5rem', width: '100%' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTeamClick(team.id);
                                                    }}
                                                >
                                                    View & Join
                                                </button>
                                            )}

                                            {team.form_link && (
                                                <button
                                                    className="btn-profile btn-profile-secondary"
                                                    style={{ marginTop: '0.5rem', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.3)' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(team.form_link.startsWith('http') ? team.form_link : `https://${team.form_link}`, '_blank');
                                                    }}
                                                >
                                                    <ExternalLink size={16} /> {team.form_title || 'Team Form'}
                                                </button>
                                            )}

                                            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--glass-border)', paddingTop: '1rem', marginTop: '0.5rem' }}>
                                                <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                                    {team.members.length} / {task.max_members} members
                                                </span>
                                                <button
                                                    className="btn-profile btn-profile-secondary"
                                                    style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTeamClick(team.id);
                                                    }}
                                                >
                                                    Details <ChevronRight size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </section>
                ) : (
                    <section className="problems-section glass-card" style={{ marginTop: '2rem', padding: '2rem', overflowX: 'auto' }}>
                        <h2 className="section-title" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FileText size={20} /> Problem Statements Overview
                        </h2>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid var(--glass-border)' }}>
                                    <th style={{ padding: '12px', color: 'var(--text-secondary)' }}>Team</th>
                                    <th style={{ padding: '12px', color: 'var(--text-secondary)' }}>Members</th>
                                    <th style={{ padding: '12px', color: 'var(--text-secondary)' }}>Problem Statements</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teams.filter(t => t.problem_statement).length === 0 ? (
                                    <tr>
                                        <td colSpan="3" style={{ padding: '24px', textAlign: 'center', opacity: 0.5 }}>
                                            No problem statements added by any team yet.
                                        </td>
                                    </tr>
                                ) : (
                                    [...teams]
                                        .filter(t => t.problem_statement)
                                        .sort((a, b) => (a.team_number || 0) - (b.team_number || 0))
                                        .map(team => {
                                            let psList = [];
                                            try {
                                                const parsed = JSON.parse(team.problem_statement);
                                                if (Array.isArray(parsed)) psList = parsed;
                                                else psList = [parsed];
                                            } catch (e) {
                                                psList = [team.problem_statement];
                                            }
                                            return (
                                                <tr key={team.id} style={{ borderBottom: '1px solid var(--glass-border)', transition: 'background-color 0.2s' }} onMouseOver={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)'} onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                    <td style={{ padding: '16px 12px', verticalAlign: 'top' }}>
                                                        <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{team.team_name}</div>
                                                        <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '4px' }}>Team #{team.team_number}</div>
                                                    </td>
                                                    <td style={{ padding: '16px 12px', verticalAlign: 'top' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                            {team.members.map(m => (
                                                                <div key={m.email} style={{ fontSize: '0.9rem' }}>
                                                                    <span style={{ opacity: 0.5, fontFamily: 'monospace' }}>{m.rollNo || 'N/A'}</span> <span style={{ opacity: 0.5 }}>-</span> {m.name}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '16px 12px', verticalAlign: 'top' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            {psList.map((ps, idx) => (
                                                                <div key={idx} style={{
                                                                    color: team.selected_ps_index === idx ? '#22c55e' : 'var(--text-main)',
                                                                    fontWeight: team.selected_ps_index === idx ? '600' : 'normal',
                                                                    display: 'flex',
                                                                    gap: '8px',
                                                                    alignItems: 'flex-start'
                                                                }}>
                                                                    <span style={{ opacity: 0.5, marginTop: '2px' }}>{idx + 1}.</span>
                                                                    <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                                                                        {team.selected_ps_index === idx && <span style={{ fontSize: '0.7rem', background: 'rgba(34,197,94,0.1)', color: '#22c55e', padding: '2px 6px', borderRadius: '4px', marginRight: '6px', verticalAlign: 'middle' }}>SELECTED</span>}
                                                                        {ps}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                )}
                            </tbody>
                        </table>
                    </section>
                    );
                })()}

                {/* Create Team Modal */}
                <AnimatePresence>
                    {showCreateModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="modal-overlay"
                            onClick={() => setShowCreateModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.9, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.9, y: 20 }}
                                className="modal-content glass-card"
                                onClick={e => e.stopPropagation()}
                                style={{ maxWidth: '400px', width: '90%' }}
                            >
                                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                                    <Sparkles size={32} color="var(--accent-color)" style={{ marginBottom: '1rem' }} />
                                    <h3 style={{ fontSize: '1.5rem' }}>Start Your Team</h3>
                                    <p style={{ opacity: 0.6 }}>Give your team a catchy name!</p>
                                </div>
                                <input
                                    type="text"
                                    className="profile-input"
                                    placeholder="Team Name (e.g. Innovators)"
                                    value={newTeamName}
                                    onChange={e => setNewTeamName(e.target.value)}
                                    autoFocus
                                />
                                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                                    <button className="btn-profile btn-profile-error" style={{ flex: 1 }} onClick={() => setShowCreateModal(false)}>Cancel</button>
                                    <button className="btn-profile btn-profile-primary" style={{ flex: 1 }} onClick={handleCreateTeam}>Create</button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
